import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2, Search } from "lucide-react";
import { useForm, useFieldArray } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Input, Select, Textarea, Card } from "@/components/ui";
import { clientsApi, lookupApi } from "@/services/api";
import { useEmailValidation } from "@/hooks";

interface ContactForm {
  name: string;
  role: string;
  email: string;
  phone: string;
  is_primary: boolean;
}

interface ClientFormData {
  name: string;
  trade_name: string;
  document: string;
  email: string;
  phone: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  address_neighborhood: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  notes: string;
  contacts: ContactForm[];
}

const BRAZILIAN_STATES = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

export default function ClientFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [isLoadingCnpj, setIsLoadingCnpj] = useState(false);

  // Refs to track last fetched values (prevent duplicate fetches)
  const lastFetchedCep = useRef<string | null>(null);
  const lastFetchedCnpj = useRef<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ClientFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      trade_name: "",
      document: "",
      email: "",
      phone: "",
      address_street: "",
      address_number: "",
      address_complement: "",
      address_neighborhood: "",
      address_city: "",
      address_state: "",
      address_zip: "",
      notes: "",
      contacts: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contacts",
  });

  // Watch email for validation
  const email = watch("email");

  // Email validation hook
  const emailValidation = useEmailValidation(email);

  // Handle CEP blur - fetch address data
  const handleCepBlur = async () => {
    const cepValue = getValues("address_zip");
    const cleanCep = cepValue.replace(/\D/g, "");

    if (cleanCep.length !== 8) return;
    if (lastFetchedCep.current === cleanCep) return;

    setIsLoadingCep(true);
    try {
      const response = await lookupApi.cep(cleanCep);

      if (response.success && response.data) {
        const data = response.data;
        if (data.street) setValue("address_street", data.street);
        if (data.neighborhood)
          setValue("address_neighborhood", data.neighborhood);
        if (data.city) setValue("address_city", data.city);
        if (data.state) setValue("address_state", data.state);
        lastFetchedCep.current = cleanCep;
        toast.success("Endereço preenchido automaticamente!");
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    } finally {
      setIsLoadingCep(false);
    }
  };

  // Handle CNPJ blur - fetch company data
  const handleCnpjBlur = async () => {
    const docValue = getValues("document");
    const cleanDoc = docValue.replace(/\D/g, "");

    if (cleanDoc.length !== 14) return; // Only CNPJ (14 digits)
    if (lastFetchedCnpj.current === cleanDoc) return;

    setIsLoadingCnpj(true);
    try {
      const response = await lookupApi.cnpj(cleanDoc);

      if (response.success && response.data) {
        const data = response.data;

        // Basic info
        if (data.razao_social) setValue("name", data.razao_social);
        if (data.nome_fantasia) setValue("trade_name", data.nome_fantasia);
        if (data.phone) setValue("phone", data.phone);
        if (data.email) setValue("email", data.email);

        // Address
        if (data.address) {
          if (data.address.street)
            setValue("address_street", data.address.street);
          if (data.address.number)
            setValue("address_number", data.address.number);
          if (data.address.complement)
            setValue("address_complement", data.address.complement);
          if (data.address.neighborhood)
            setValue("address_neighborhood", data.address.neighborhood);
          if (data.address.city) setValue("address_city", data.address.city);
          if (data.address.state) setValue("address_state", data.address.state);
          if (data.address.cep) setValue("address_zip", data.address.cep);
        }

        // Create contacts from partners (sócios)
        if (data.partners && data.partners.length > 0) {
          const contacts = data.partners.map((partner, index) => ({
            name: partner.name,
            role: partner.role,
            email: "",
            phone: "",
            is_primary: index === 0,
          }));
          setValue("contacts", contacts);
        }

        lastFetchedCnpj.current = cleanDoc;
        toast.success("Dados do CNPJ preenchidos automaticamente!");
      }
    } catch (error) {
      console.error("Erro ao buscar CNPJ:", error);
      toast.error("Erro ao buscar dados do CNPJ");
    } finally {
      setIsLoadingCnpj(false);
    }
  };

  const { data: clientData, isLoading: isLoadingClient } = useQuery({
    queryKey: ["client", id],
    queryFn: () => clientsApi.get(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (clientData?.data) {
      const client = clientData.data;

      // Set last fetched values to prevent auto-fetch when loading existing data
      lastFetchedCep.current = client.address_zip?.replace(/\D/g, "") || null;
      lastFetchedCnpj.current = client.document?.replace(/\D/g, "") || null;

      reset({
        name: client.name || "",
        trade_name: client.trade_name || "",
        document: client.document || "",
        email: client.email || "",
        phone: client.phone || "",
        address_street: client.address_street || "",
        address_number: client.address_number || "",
        address_complement: client.address_complement || "",
        address_neighborhood: client.address_neighborhood || "",
        address_city: client.address_city || "",
        address_state: client.address_state || "",
        address_zip: client.address_zip || "",
        notes: client.notes || "",
        contacts: client.contacts || [],
      });
    }
  }, [clientData, reset]);

  const createMutation = useMutation({
    mutationFn: clientsApi.create,
    onSuccess: () => {
      toast.success("Cliente criado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      navigate("/admin/clients");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Erro ao criar cliente");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ClientFormData) => clientsApi.update(id!, data),
    onSuccess: () => {
      toast.success("Cliente atualizado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", id] });
      navigate("/admin/clients");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Erro ao atualizar cliente",
      );
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: ClientFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const addContact = () => {
    append({
      name: "",
      role: "",
      email: "",
      phone: "",
      is_primary: fields.length === 0,
    });
  };

  if (isEditing && isLoadingClient) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/clients")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-text-primary">
            {isEditing ? "Editar Cliente" : "Novo Cliente"}
          </h1>
          <p className="text-text-secondary mt-1">
            {isEditing
              ? "Atualize as informações do cliente"
              : "Cadastre um novo cliente"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Informações Básicas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Razão Social / Nome *"
              format="name"
              {...register("name", { required: "Nome é obrigatório" })}
              error={errors.name?.message}
            />
            <Input
              label="Nome Fantasia"
              format="name"
              {...register("trade_name")}
            />
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  label="CPF / CNPJ"
                  format="document"
                  {...register("document")}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  hint={
                    isLoadingCnpj
                      ? "Buscando dados do CNPJ..."
                      : "Digite um CNPJ e clique em Buscar"
                  }
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCnpjBlur}
                disabled={isLoadingCnpj}
                className="mt-6 h-10"
              >
                <Search className="w-4 h-4 mr-1" />
                {isLoadingCnpj ? "..." : "Buscar"}
              </Button>
            </div>
            <Input
              label="Email *"
              type="email"
              format="email"
              {...register("email", {
                required: "Email é obrigatório",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Email inválido",
                },
              })}
              error={errors.email?.message}
              hint={
                emailValidation.isValidating
                  ? "Validando..."
                  : emailValidation.message
              }
              className={
                emailValidation.valid === false
                  ? "border-red-500"
                  : emailValidation.valid === true
                    ? "border-green-500"
                    : ""
              }
            />
            <Input
              label="Telefone"
              format="phone"
              {...register("phone")}
              placeholder="(00) 00000-0000"
              hint="Formatação automática - celular ou fixo"
            />
          </div>
        </Card>

        {/* Address */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Endereço
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex gap-2 md:col-span-1">
              <div className="flex-1">
                <Input
                  label="CEP"
                  format="cep"
                  {...register("address_zip")}
                  placeholder="00000-000"
                  hint={
                    isLoadingCep
                      ? "Buscando endereço..."
                      : "Digite o CEP e clique em Buscar"
                  }
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCepBlur}
                disabled={isLoadingCep}
                className="mt-6 h-10"
              >
                <Search className="w-4 h-4 mr-1" />
                {isLoadingCep ? "..." : "Buscar"}
              </Button>
            </div>
            <Input
              label="Rua"
              format="name"
              {...register("address_street")}
              className="md:col-span-2"
            />
            <Input label="Número" {...register("address_number")} />
            <Input
              label="Complemento"
              format="name"
              {...register("address_complement")}
            />
            <Input
              label="Bairro"
              format="name"
              {...register("address_neighborhood")}
            />
            <Input label="Cidade" format="name" {...register("address_city")} />
            <Select
              label="Estado"
              {...register("address_state")}
              options={BRAZILIAN_STATES.map((state) => ({
                value: state,
                label: state,
              }))}
              placeholder="Selecione..."
            />
          </div>
        </Card>

        {/* Contacts */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">
              Contatos
            </h2>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addContact}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Contato
            </Button>
          </div>

          {fields.length === 0 ? (
            <p className="text-text-muted text-center py-8">
              Nenhum contato cadastrado. Clique em "Adicionar Contato" para
              começar.
            </p>
          ) : (
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="p-4 bg-dark-lighter rounded-lg border border-white/5"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-secondary">
                        Contato {index + 1}
                      </span>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          {...register(`contacts.${index}.is_primary`)}
                          className="checkbox"
                        />
                        <span className="text-sm text-text-muted">
                          Principal
                        </span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-2 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Nome"
                      format="name"
                      {...register(`contacts.${index}.name`)}
                    />
                    <Input
                      label="Cargo"
                      format="name"
                      {...register(`contacts.${index}.role`)}
                    />
                    <Input
                      label="Email"
                      type="email"
                      format="email"
                      {...register(`contacts.${index}.email`)}
                    />
                    <Input
                      label="Telefone"
                      format="phone"
                      {...register(`contacts.${index}.phone`)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Notes */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Observações
          </h2>
          <Textarea
            {...register("notes")}
            placeholder="Anotações sobre o cliente..."
            rows={4}
          />
        </Card>

        {/* Login Info */}
        {isEditing && (
          <Card className="p-6 bg-gradient-to-br from-accent-primary/10 to-accent-secondary/10 border-accent-primary/20">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-text-primary mb-2">
                  💡 Acesso ao Portal do Cliente
                </h2>
                <p className="text-text-secondary mb-4">
                  Para dar acesso ao cliente ao portal de orçamentos, crie um
                  usuário vinculado a este cliente.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(`/admin/users/new?client_id=${id}`)}
                >
                  Criar Usuário para este Cliente
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/admin/clients")}
          >
            Cancelar
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {isEditing ? "Salvar Alterações" : "Cadastrar Cliente"}
          </Button>
        </div>
      </form>
    </div>
  );
}
